/**
 * SoundManager - Central sound effects system for dopamine hits
 *
 * Usage:
 *   import { soundManager, SOUNDS } from '../systems/SoundManager';
 *   soundManager.play(SOUNDS.MONEY_LAND);
 */

import { debug } from '../lib/debug.js';

class SoundManager {
  constructor() {
    this.sounds = {};
    this.volume = 0.7;
    this.muted = false;
    this.loaded = false;
    this.loadingPromise = null;
  }

  async init() {
    if (this.loadingPromise) return this.loadingPromise;

    this.loadingPromise = this._loadAllSounds();
    return this.loadingPromise;
  }

  async _loadAllSounds() {
    const soundFiles = {
      // Money moments
      // MONEY_LAND: './audio/sfx/money_land.wav', // TODO: File doesn't exist
      MONEY_DAILY: './audio/sfx/money_daily.wav',
      ROUTE_PROFITABLE: './audio/sfx/route_profitable.wav',

      // Progression moments
      TIER_UP: './audio/sfx/tier_up.wav',
      SLOT_WON: './audio/sfx/slot_won.wav',
      HUB_WON: './audio/sfx/hub_won.wav',
      EOS_CHECK: './audio/sfx/eos_check.wav',
      EOS_DAY: './audio/sfx/eos_day.wav',

      // Collectible moments
      ROLL_START: './audio/sfx/roll_start.wav',
      DROP_RARE: './audio/sfx/drop_rare.wav',
      DROP_RELIC: './audio/sfx/drop_relic.wav',
      DROP_ANOMALOUS: './audio/sfx/drop_anomalous.wav',
      DROP_LEGENDARY: './audio/sfx/drop_legendary.wav',
      DROP_MYTHIC: './audio/sfx/drop_mythic.wav',

      // Operational moments
      FLIGHT_DEPART: './audio/sfx/flight_depart.wav',
      FLIGHT_LAND: './audio/sfx/flight_land.wav',
      TASK_COMPLETE: './audio/sfx/task_complete.wav',
      BATCH_COMPLETE: './audio/sfx/batch_complete.wav',

      // Warnings/Alerts
      WARNING: './audio/sfx/warning.wav',
      ALERT_CRITICAL: './audio/sfx/alert_critical.wav',
      CREW_ILLEGAL: './audio/sfx/crew_illegal.wav',
      MECHANICAL_ISSUE: './audio/sfx/mechanical_issue.wav',

      // UI feedback
      CLICK: './audio/sfx/click.wav',
      SUCCESS: './audio/sfx/success.wav',
      ERROR: './audio/sfx/error.wav',

      // Intro sequence (using _1 versions without pops)
      INTRO_AMBIENT: './audio/sfx/intro_ambient_1.wav',      // Cockpit ambiance loop
      INTRO_WHOOSH: './audio/sfx/intro.whoosh.wav',        // Screen transitions
      INTRO_MYTHIC_RUMBLE: './audio/sfx/intro_mythic_1.wav', // Deep rumble for mythic reveal
      INTRO_BEGIN: './audio/sfx/intro_begin2.wav'            // Satisfying BEGIN button (cleaned)
    };

    const loadPromises = Object.entries(soundFiles).map(async ([key, path]) => {
      try {
        const audio = new Audio(path);
        audio.preload = 'auto';

        // Wait for audio to be loadable (or fail gracefully)
        await new Promise((resolve) => {
          audio.addEventListener('canplaythrough', resolve, { once: true });
          audio.addEventListener('error', () => {
            console.warn(`Failed to load sound: ${key} (${path})`);
            resolve();
          }, { once: true });

          // Timeout after 5 seconds
          setTimeout(resolve, 5000);
        });

        this.sounds[key] = audio;
      } catch (e) {
        console.warn(`Failed to load sound: ${key}`, e);
      }
    });

    await Promise.all(loadPromises);
    this.loaded = true;
    debug(`SoundManager: Loaded ${Object.keys(this.sounds).length} sounds`);
  }

  play(soundId, options = {}) {
    if (this.muted) return;

    const sound = this.sounds[soundId];
    if (!sound) {
      // Silently fail if sound not loaded yet - don't spam console
      return;
    }

    try {
      // Clone the audio for overlapping sounds
      const playableSound = sound.cloneNode();
      playableSound.volume = options.volume !== undefined
        ? options.volume * this.volume
        : this.volume;

      playableSound.currentTime = 0;

      // MEMORY LEAK FIX: Clean up audio element after playback completes
      const cleanup = () => {
        try {
          playableSound.removeAttribute('src');
          playableSound.load();
          playableSound.remove();
        } catch (e) {
          // Silently handle cleanup errors
        }
      };

      playableSound.addEventListener('ended', cleanup, { once: true });
      playableSound.addEventListener('error', cleanup, { once: true });

      const playPromise = playableSound.play();
      if (playPromise) {
        playPromise.catch(e => {
          // Autoplay policy blocked - user hasn't interacted yet
          // This is expected on first load, don't warn
          cleanup(); // Clean up on play failure too
        });
      }
    } catch (e) {
      // Silently fail
    }
  }

  // Play with a slight random pitch variation for variety
  playVaried(soundId, pitchRange = 0.1) {
    if (this.muted) return;

    const sound = this.sounds[soundId];
    if (!sound) return;

    try {
      const playableSound = sound.cloneNode();
      playableSound.volume = this.volume;

      // Slight playback rate variation (0.9 - 1.1 by default)
      playableSound.playbackRate = 1 + (Math.random() - 0.5) * 2 * pitchRange;

      playableSound.currentTime = 0;

      // MEMORY LEAK FIX: Clean up audio element after playback completes
      const cleanup = () => {
        try {
          playableSound.removeAttribute('src');
          playableSound.load();
          playableSound.remove();
        } catch (e) {
          // Silently handle cleanup errors
        }
      };

      playableSound.addEventListener('ended', cleanup, { once: true });
      playableSound.addEventListener('error', cleanup, { once: true });

      playableSound.play().catch(e => {
        cleanup(); // Clean up on play failure too
      });
    } catch (e) {
      // Silently fail
    }
  }

  setVolume(level) {
    this.volume = Math.max(0, Math.min(1, level));
    localStorage.setItem('sfxVolume', this.volume.toString());
  }

  getVolume() {
    return this.volume;
  }

  toggleMute() {
    this.muted = !this.muted;
    localStorage.setItem('sfxMuted', this.muted.toString());
    return this.muted;
  }

  setMuted(muted) {
    this.muted = muted;
    localStorage.setItem('sfxMuted', this.muted.toString());
  }

  isMuted() {
    return this.muted;
  }

  // Play a looping sound (returns handle to stop it)
  playLoop(soundId, options = {}) {
    if (this.muted) return null;

    const sound = this.sounds[soundId];
    if (!sound) return null;

    try {
      const loopSound = sound.cloneNode();
      loopSound.volume = options.volume !== undefined
        ? options.volume * this.volume
        : this.volume;
      loopSound.loop = true;
      loopSound.currentTime = 0;

      // Store reference to stop later
      if (!this.loopingSounds) this.loopingSounds = {};
      this.loopingSounds[soundId] = loopSound;

      loopSound.play().catch(() => {});
      return loopSound;
    } catch (e) {
      return null;
    }
  }

  // Stop a looping sound
  stop(soundId) {
    if (!this.loopingSounds || !this.loopingSounds[soundId]) return;

    try {
      const loopSound = this.loopingSounds[soundId];
      loopSound.pause();
      loopSound.currentTime = 0;

      // MEMORY LEAK FIX: Properly clean up audio element
      loopSound.removeAttribute('src');
      loopSound.load();
      loopSound.remove();

      delete this.loopingSounds[soundId];
    } catch (e) {
      // Silently fail
    }
  }

  // Set volume of a looping sound
  setLoopVolume(soundId, volume) {
    if (!this.loopingSounds || !this.loopingSounds[soundId]) return;

    try {
      this.loopingSounds[soundId].volume = volume * this.volume;
    } catch (e) {
      // Silently fail
    }
  }

  loadPreferences() {
    const savedVolume = localStorage.getItem('sfxVolume');
    const savedMuted = localStorage.getItem('sfxMuted');

    if (savedVolume !== null) {
      this.volume = parseFloat(savedVolume);
    }
    if (savedMuted !== null) {
      this.muted = savedMuted === 'true';
    }
  }
}

// Export singleton
export const soundManager = new SoundManager();

// Sound IDs for type safety and autocomplete
export const SOUNDS = {
  // Money moments
  MONEY_LAND: 'MONEY_LAND',
  MONEY_DAILY: 'MONEY_DAILY',
  ROUTE_PROFITABLE: 'ROUTE_PROFITABLE',

  // Progression moments
  TIER_UP: 'TIER_UP',
  SLOT_WON: 'SLOT_WON',
  HUB_WON: 'HUB_WON',
  EOS_CHECK: 'EOS_CHECK',
  EOS_DAY: 'EOS_DAY',

  // Collectible moments
  ROLL_START: 'ROLL_START',
  DROP_RARE: 'DROP_RARE',
  DROP_RELIC: 'DROP_RELIC',
  DROP_ANOMALOUS: 'DROP_ANOMALOUS',
  DROP_LEGENDARY: 'DROP_LEGENDARY',
  DROP_MYTHIC: 'DROP_MYTHIC',

  // Operational moments
  FLIGHT_DEPART: 'FLIGHT_DEPART',
  FLIGHT_LAND: 'FLIGHT_LAND',
  TASK_COMPLETE: 'TASK_COMPLETE',
  BATCH_COMPLETE: 'BATCH_COMPLETE',

  // Warnings/Alerts
  WARNING: 'WARNING',
  ALERT_CRITICAL: 'ALERT_CRITICAL',
  CREW_ILLEGAL: 'CREW_ILLEGAL',
  MECHANICAL_ISSUE: 'MECHANICAL_ISSUE',

  // UI feedback
  CLICK: 'CLICK',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',

  // Intro sequence
  INTRO_AMBIENT: 'INTRO_AMBIENT',
  INTRO_WHOOSH: 'INTRO_WHOOSH',
  INTRO_MYTHIC_RUMBLE: 'INTRO_MYTHIC_RUMBLE',
  INTRO_BEGIN: 'INTRO_BEGIN'
};

export default soundManager;
